import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Briefcase, MessageSquare, FileText, LogOut, Menu, X } from 'lucide-react';

const MOBILE = '900px';

const TopBar = styled.div`
  display: none;

  @media (max-width: ${MOBILE}) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 14px;
    background: ${({ theme }) => theme.colors.surface};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    position: sticky;
    top: 0;
    z-index: 60;
  }
`;

const TopBarBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
`;

const IconButton = styled.button`
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: background 160ms ease, border-color 160ms ease;

  &:hover {
    background: ${({ theme }) => `rgba(${theme.colors.textRgb}, 0.06)`};
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }

  &:active { transform: scale(0.96); }
`;

const Backdrop = styled(motion.div)`
  display: none;

  @media (max-width: ${MOBILE}) {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    z-index: 90;
  }
`;

const Aside = styled(motion.aside)`
  width: 240px;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.surface};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  z-index: 50;

  @media (max-width: ${MOBILE}) {
    position: fixed;
    inset: 0 auto 0 0;
    width: min(82vw, 320px);
    min-height: 100vh;
    height: 100vh;
    z-index: 100;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.4);
    overflow-y: auto;
  }
`;

const Brand = styled.div`
  padding: 26px 22px 22px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;

  @media (max-width: ${MOBILE}) {
    padding: 18px 18px 16px;
  }
`;

const BrandText = styled.div`
  min-width: 0;
`;

const LogoMark = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LogoImg = styled.img`
  height: 32px;
  width: auto;
  display: block;

  @media (max-width: ${MOBILE}) {
    height: 26px;
  }
`;

const Wordmark = styled.h1`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;

  .amp {
    font-family: 'DM Mono', monospace;
    font-weight: 500;
    padding: 0 1px;
  }
`;

const Label = styled.span`
  display: block;
  margin-top: 6px;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.text.muted};
  text-transform: uppercase;
  letter-spacing: 0.18em;
`;

const DrawerClose = styled.button`
  display: none;

  @media (max-width: ${MOBILE}) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: transparent;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 8px;
    color: ${({ theme }) => theme.colors.text.secondary};
    cursor: pointer;
  }
`;

const Nav = styled.nav`
  flex: 1;
  padding: 14px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const NavItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 14px;
  background: ${({ theme, $active }) =>
    $active ? `rgba(${theme.colors.textRgb}, 0.06)` : 'transparent'};
  border: none;
  border-left: 2px solid ${({ theme, $active }) =>
    $active ? theme.colors.accent : 'transparent'};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.text.primary : theme.colors.text.secondary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.93rem;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  border-radius: 0 8px 8px 0;
  transition: background 160ms ease, color 160ms ease;
  min-height: 44px;

  &:hover {
    background: ${({ theme }) => `rgba(${theme.colors.textRgb}, 0.06)`};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Footer = styled.div`
  padding: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ToolBtn = styled(motion.button)`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.88rem;
  cursor: pointer;
  min-height: 44px;
  transition: background 160ms ease, color 160ms ease, border-color 160ms ease;

  &:hover {
    background: ${({ theme }) => `rgba(${theme.colors.textRgb}, 0.04)`};
    color: ${({ theme }) => theme.colors.text.primary};
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
`;

const LogoutBtn = styled(ToolBtn)`
  color: #ef4444;
  border-color: rgba(239, 68, 68, 0.25);

  &:hover {
    background: rgba(239, 68, 68, 0.08);
    color: #ef4444;
    border-color: rgba(239, 68, 68, 0.45);
  }
`;

const DesktopOnly = styled.div`
  display: contents;

  @media (max-width: ${MOBILE}) {
    display: none;
  }
`;

const ITEMS = [
  { id: 'portfolio', label: 'Portfolio', icon: Briefcase },
  { id: 'chatbot', label: 'Chatbot AI', icon: MessageSquare },
  { id: 'proposal', label: 'Proposal Machine', icon: FileText },
];

const CMSSidebar = ({ active, onChange, onLogout, isOpen, onOpen, onClose }) => {
  const currentLabel = ITEMS.find((i) => i.id === active)?.label || 'CMS';

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  const handleNavSelect = (id) => {
    onChange(id);
    onClose?.();
  };

  const sidebarBody = (
    <>
      <Brand>
        <BrandText>
          <LogoMark>
            <LogoImg src="/cnd-logo.png" alt="C&D Software logo" />
            <Wordmark>C<span className="amp">&amp;</span>D Software</Wordmark>
          </LogoMark>
          <Label>Admin CMS</Label>
        </BrandText>
        <DrawerClose onClick={onClose} aria-label="Close menu">
          <X size={18} />
        </DrawerClose>
      </Brand>
      <Nav>
        {ITEMS.map(({ id, label, icon: Icon }) => (
          <NavItem
            key={id}
            $active={active === id}
            onClick={() => handleNavSelect(id)}
          >
            <Icon size={17} />
            {label}
          </NavItem>
        ))}
      </Nav>
      <Footer>
        <LogoutBtn onClick={onLogout} whileTap={{ scale: 0.98 }}>
          <LogOut size={15} />
          Logout
        </LogoutBtn>
      </Footer>
    </>
  );

  return (
    <>
      <TopBar>
        <IconButton onClick={onOpen} aria-label="Open menu">
          <Menu size={20} />
        </IconButton>
        <TopBarBrand>
          <LogoImg src="/cnd-logo.png" alt="C&D Software logo" />
          <Wordmark>{currentLabel}</Wordmark>
        </TopBarBrand>
        <IconButton onClick={onLogout} aria-label="Logout" style={{ color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)' }}>
          <LogOut size={18} />
        </IconButton>
      </TopBar>

      <DesktopOnly>
        <Aside>{sidebarBody}</Aside>
      </DesktopOnly>

      <AnimatePresence>
        {isOpen && (
          <>
            <Backdrop
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              onClick={onClose}
            />
            <Aside
              key="drawer"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.22, ease: 'easeOut' }}
            >
              {sidebarBody}
            </Aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default CMSSidebar;
